// Merchant session — kind:27235 challenge-response handshake.
// Establishes synvya_merchant_session HttpOnly cookie (15min TTL).
//
// Thin delegate over @synvya/auth-identity's shared MerchantSessionClient
// (auth-identity#25) — the handshake logic lives in one place now. The
// module-level singleton preserves the concurrent-dedup semantics (the
// client owns the in-flight handshake); in jsdom/browser it auto-selects
// `credentials: 'include'` mode, matching account's serverFetch.

import { MerchantSessionClient } from '@synvya/auth-identity/merchant-session'
import { getServerBaseUrl } from '@/lib/baseUrl'
import type { EventTemplate, VerifiedEvent } from 'nostr-tools'

type SignFn = (template: EventTemplate) => Promise<VerifiedEvent>

interface ChallengeResponse {
  challenge_id: string
  merchant_pubkey: string
  nonce: string
  issued_at: string
  expires_at: string
}

/** Build the kind:27235 auth event template for a challenge. */
export function buildMerchantAuthTemplate(
  challenge: ChallengeResponse
): EventTemplate {
  return {
    kind: 27235,
    created_at: Math.floor(Date.now() / 1000),
    tags: [
      ['challenge_id', challenge.challenge_id],
      ['nonce', challenge.nonce],
      ['merchant_pubkey', challenge.merchant_pubkey],
      ['expires_at', challenge.expires_at],
    ],
    content: '',
  }
}

// Lazily constructed so the base URL is resolved at handshake time (as it
// was inside serverFetch), not at module import — importing this module must
// not require VITE_SERVER_BASE_URL to be set.
let client: MerchantSessionClient | null = null

function getClient(): MerchantSessionClient {
  if (!client) {
    client = new MerchantSessionClient({ baseUrl: getServerBaseUrl() })
  }
  return client
}

/**
 * Ensure a valid merchant session cookie exists.
 * If the cookie is expired or missing, runs the challenge-response handshake.
 * Deduplicates concurrent calls.
 */
export async function ensureMerchantSession(
  merchantPubkey: string,
  signAsRestaurant: SignFn
): Promise<void> {
  return getClient().ensureSession(merchantPubkey, signAsRestaurant)
}
